<template>
  <div class="agreement-details-shell">
    <!-- Container for the entire agreement details shell -->
    <div class="agreement-details-shell__container">
      <!-- Main Content Area -->
      <div class="agreement-details-shell__content">
        <!-- Left Side: Agreement Details -->
        <div class="agreement-details-shell__content--left">
          <AgreementDetail
            v-if="
              !state.isLoadingAgreement &&
              props.acceptedServiceOffer &&
              state.serviceActivitiesCms
            "
            :agreement-details="agreementDetails"
            :action-buttons="actionButtons"
            :attachments="attachments"
            :tender="tender"
            :supplier-id="supplierId"
            :service-activities-cms="state.serviceActivitiesCms"
          />
          <AgreementDetailSkeleton v-else />
        </div>

        <!-- Right Side: Invoice & Customer Info -->
        <div class="agreement-details-shell__content--right">
          <InvoiceRecipient
            :booking-number="bookingNumber"
            :by-pass-really="invoiceRecipientLogic.shouldBypassReally.value"
            :recipient="invoiceRecipientData.recipient"
            :is-loading="invoiceRecipientData.isLoading"
            :error="invoiceRecipientData.error"
          />
          <CustomerContactInfo
            :customer-details="customerDetailsFromAcceptedUser"
            :is-loading="isLoadingCustomerDetails"
          />
        </div>
      </div>

      <!-- Bottom Section: Service Offers -->
      <div class="agreement-details-shell__bottom">
        <ServiceOffersShell
          :tender="tender"
          :supplier-id="supplierId"
          :supplier-request-id="supplierRequestId"
          :service-offers="serviceOffers"
          :frequency-options="frequencyOptions"
          :is-estimate-request="isEstimateRequest"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * AgreementDetailsShell Component
 *
 * A comprehensive component that manages agreement details display, including:
 * - Invoice recipient information with fallback logic
 * - Customer contact details with caching
 * - Service activities CMS data
 * - Extended billable state management
 * - Cookie-based caching for performance optimization
 *
 * The component follows Vue 3 Composition API best practices with:
 * - Clear separation of concerns
 * - Type-safe interfaces
 * - Performance optimizations through caching
 * - Reactive state management
 * - Exposed utilities for external access
 */
import { computed, onMounted, watch, reactive } from "vue";
import InvoiceRecipient from "./InvoiceRecipient.vue";
import AgreementDetail from "./AgreementDetail.vue";
import CustomerContactInfo from "./CustomerContactInfo.vue";
import AgreementDetailSkeleton from "./AgreementDetailSkeleton.vue";
import ServiceOffersShell from "@/components/ServiceOffers/ServiceOffersShell.vue";
import { FeedbackType } from "@/custom-types/GeneralTypes";
import { useInvoiceRecipientLogic } from "@/composables/useInvoiceRecipientLogic";
import { useCmsStore } from "@/stores/cms";
import { ContentType } from "@/custom-types/CmsContentTypes";
import type {
  Tender,
  ServiceOffer as ServiceOfferType,
} from "@/stores/tender/types";
import type { InvoiceRecipient as InvoiceRecipientType } from "@/stores/tender/types";
import { useExtendedBillable } from "@/composables/useExtendedBillable";
import type { SignatureUsers } from "@/composables/useExtendedBillable";
import type { ServiceOffer } from "@/custom-types/GeneralTypes";
import { agreementCache } from "@/services/agreementCache";
import { createCookieCache } from "@/utilities/cookieUtils";
import cmsService from "@/services/cmsService";

// ===== TYPES AND INTERFACES =====
interface Props {
  tender: Tender;
  supplierId: number;
  frequencyOptions: {
    label: string;
    value: string;
  };
  serviceOfferId: number;
  supplierRequestId: number;
  bookingNumber: string;
  acceptedServiceOffer?: ServiceOfferType | null;
  serviceOffers: ServiceOffer[];
  priceRequestId?: number | undefined;
  isEstimateRequest: boolean;
}

interface CustomerDetails {
  displayName: string | null;
  email: string | null;
  mobilePhone: string | null;
}

interface ServiceActivityCms {
  title: string;
  label: string;
  [key: string]: unknown;
}

interface AgreementDetails {
  nickname: string;
  supplierCost: string;
  supplierCostSubtitle: string;
  serviceCategoryTitle: string;
  suggestedStartDate: string;
  signatureUsers: SignatureUsers[];
  averageCost: string | null;
  acceptedServiceOffer: ServiceOfferType | null;
}

interface ComponentState {
  isLoading: boolean;
  errorMessage: {
    type: FeedbackType;
    messageTitle: string;
    messageInfo: string;
  } | null;
  company: InvoiceRecipientType | null;
  isLoadingAgreement: boolean;
  serviceActivitiesCms: ServiceActivityCms[] | null;
}

// ===== PROPS AND DEFAULTS =====
const props = withDefaults(defineProps<Props>(), {
  frequencyOptions: () => ({ label: "", value: "" }),
  acceptedServiceOffer: null,
  serviceOffers: () => [],
  priceRequestId: undefined,
  isEstimateRequest: false,
});

// ===== COMPOSABLES AND SERVICES =====
const cmsStore = useCmsStore();

// Initialize invoice recipient logic composable
const invoiceRecipientLogic = useInvoiceRecipientLogic();

// ===== CACHE CONFIGURATION =====
const CACHE_DURATION = 5 / 1440; // 5 minutes in days

const cacheConfig = {
  // Note: serviceActivitiesCms cache removed due to data size exceeding cookie limits
  company: createCookieCache("company", CACHE_DURATION),
};

// ===== REACTIVE STATE =====
const state = reactive<ComponentState>({
  isLoading: false,
  errorMessage: null,
  company: null,
  isLoadingAgreement: false,
  serviceActivitiesCms: null,
});

// Transform CMS ServiceType data to the format expected by useExtendedBillable
const transformServiceTypesData = () => {
  const cmsServiceTypes = cmsStore[ContentType.SERVICE_TYPES_CMS];
  if (!cmsServiceTypes) return [];

  const transformed = cmsServiceTypes.map((serviceType) => ({
    label: serviceType.label || "",
    serviceCategories: (serviceType.serviceCategories || []).map(
      (category) => ({
        label: category.label || "",
        title: category.title || "",
      }),
    ),
  }));

  return transformed;
};

const {
  actionButtons,
  initialize,
  state: extendedBillableState,
  signatureUsers,
} = useExtendedBillable({
  tender: props.tender,
  supplierId: props.supplierId,
  serviceTypesCms: transformServiceTypesData(), // Transform CMS data to expected format
});

// Computed property to transform acceptedByUser to CustomerDetails format
const customerDetailsFromAcceptedUser = computed((): CustomerDetails | null => {
  const acceptedByUser = extendedBillableState.acceptedByUser;
  if (!acceptedByUser) return null;

  return {
    displayName: acceptedByUser.displayName || null,
    email: acceptedByUser.email || null,
    mobilePhone: acceptedByUser.mobilePhone || null,
  };
});

// Check if we're loading user data from the extended billable composable
const isLoadingCustomerDetails = computed(() => {
  // We're loading if the extended billable is still loading frame agreement data
  // or if we have an accepted service offer but no user data yet
  const hasServiceOffer = !!extendedBillableState.acceptedServiceOffer;
  const hasUserData = !!extendedBillableState.acceptedByUser;
  const isLoadingFrameAgreement = extendedBillableState.isLoadingFrameAgreement;

  return isLoadingFrameAgreement || (hasServiceOffer && !hasUserData);
});

// Computed properties for invoice recipient parameters
const invoiceRecipientParams = computed(() => {
  if (!props.tender?.priceRequests?.[0]) {
    return null;
  }

  const priceRequest = props.tender.priceRequests[0];
  return {
    supplierId: props.supplierId,
    priceRequestId: priceRequest.id,
    customerPropertyId: priceRequest.customerPropertyId,
    orgNumber: props.tender.customerOrganization.orgNumber,
  };
});

// Computed properties for tender invoice data
const tenderInvoiceData = computed(() => ({
  invoiceRecipientName: props.tender.invoiceRecipientName,
  invoiceRecipientEmail: props.tender.invoiceRecipientEmail,
  invoiceRecipientOrgNumber: props.tender.invoiceRecipientOrgNumber,
  externalTenderId: props.tender.externalTenderId,
}));

// Watch for changes and initialize invoice recipient logic
watch(
  () =>
    [
      props.tender?.customerOrganization?.orgNumber,
      props.acceptedServiceOffer?.integrationSource,
      invoiceRecipientParams.value,
    ] as const,
  async ([orgNumber, integrationSource, params]) => {
    if (orgNumber && params) {
      await invoiceRecipientLogic.initialize({
        orgNumber,
        integrationSource: integrationSource || null,
        invoiceRecipientParams: params,
        tenderData: tenderInvoiceData.value,
        bookingNumber: props.bookingNumber,
        tenderBypassReally: props.tender?.byPassReallyInvoicing || false,
      });
    }
  },
  { immediate: true },
);

// Get invoice recipient data from the new logic
const invoiceRecipientData = computed(() => {
  return invoiceRecipientLogic.invoiceRecipientData.value;
});

// Sync invoice recipient data with company state for customer resolution
watch(
  () => invoiceRecipientData.value.recipient,
  (recipient) => {
    if (recipient && invoiceRecipientLogic.shouldBypassReally.value) {
      // Convert the invoice recipient result to company format
      state.company = {
        email: recipient.email,
        receivingOrganization: recipient.receivingOrganization,
      } as InvoiceRecipientType;

      // Cache the company data
      const tenderId = props.tender?.tenderId;
      if (tenderId && state.company) {
        cacheConfig.company.saveToCache(tenderId, state.company);
      }
    }
  },
  { immediate: true },
);

// Check if we have cached data and use it
/**
 * Checks cache and initializes extended billable state for a service offer
 * @param serviceOffer - The service offer to initialize
 */
const checkCacheAndInitialize = async (
  serviceOffer: ServiceOfferType,
): Promise<void> => {
  if (!serviceOffer) return;

  const tenderId = props.tender?.tenderId;
  if (!tenderId) return;

  state.isLoadingAgreement = true;

  try {
    // Check if we have cached data
    const cachedData = agreementCache.get(
      tenderId,
      props.supplierId,
      props.serviceOfferId,
    );

    if (cachedData) {
      // Apply cached data directly
      Object.assign(extendedBillableState, cachedData);
    } else {
      // Initialize from API and cache the result
      await initialize(serviceOffer);

      // Store the processed data in cache
      agreementCache.set(tenderId, props.supplierId, props.serviceOfferId, {
        ...extendedBillableState,
      });
    }
  } finally {
    state.isLoadingAgreement = false;
  }
};

// Watch for changes in acceptedServiceOffer and reinitialize when it changes
watch(
  () => props.acceptedServiceOffer,
  async (newOffer) => {
    if (newOffer) {
      await checkCacheAndInitialize(newOffer);
    }
  },
  { immediate: true },
);

const agreementDetails = computed<AgreementDetails>(() => ({
  nickname: extendedBillableState.nickname,
  supplierCost: extendedBillableState.supplierCost,
  supplierCostSubtitle: extendedBillableState.supplierCostSubtitle,
  serviceCategoryTitle: extendedBillableState.serviceCategoryTitle,
  suggestedStartDate: extendedBillableState.suggestedStartDate,
  signatureUsers: signatureUsers.value,
  averageCost: extendedBillableState.averageCost,
  acceptedServiceOffer: extendedBillableState.acceptedServiceOffer,
}));

const attachments = computed(
  () =>
    extendedBillableState.acceptedServiceOffer?.serviceOfferAttachments ?? [],
);

// ===== BUSINESS LOGIC FUNCTIONS =====

/**
 * Cache management utilities for clearing cached data
 * Exposed for external use by parent components or testing
 */
const cacheUtils = {
  clearCompanyData: () => {
    const tenderId = props.tender?.tenderId;
    if (tenderId) {
      cacheConfig.company.removeFromCache(tenderId);
    }
  },

  clearAll: () => {
    const tenderId = props.tender?.tenderId;
    if (tenderId) {
      cacheConfig.company.removeFromCache(tenderId);
    }
  },
};

onMounted(async () => {
  state.isLoading = true;

  try {
    const tenderId = props.tender?.tenderId;
    if (!tenderId) return;

    const serviceActivitiesCms =
      (await cmsService.getServiceActivities()) as ServiceActivityCms[];
    state.serviceActivitiesCms = serviceActivitiesCms;

    // Company data - check cache first, but note that the invoice recipient
    // logic will also populate this via the watcher above
    const cachedCompany =
      cacheConfig.company.getFromCache<InvoiceRecipientType>(tenderId);

    if (cachedCompany) {
      state.company = cachedCompany;
    }
    // Note: If no cached data, the invoice recipient logic watcher
    // will populate state.company when data becomes available
  } catch (e) {
    console.error("Error in onMounted:", e);
    state.errorMessage = {
      type: FeedbackType.ERROR,
      messageTitle: "Feil ved lasting av data",
      messageInfo: "Kunne ikke laste inn data. Prøv å laste siden på nytt.",
    };
  } finally {
    state.isLoading = false;
  }
});

// Expose utilities for external access (testing, debugging, parent components)
defineExpose({
  cacheUtils,
  state,
});
</script>

<style lang="scss" scoped>
.agreement-details-shell {
  min-height: 22rem;
  padding: 0 0;
  width: 100%;

  &__container {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    max-width: 1060px;
    margin: auto;

    @media (max-width: $bp-sm) {
      padding: 1rem;
      gap: 1.5rem;
    }
  }

  &__content {
    display: flex;
    gap: 1rem;
    width: 100%;

    &--left {
      flex: 3;
      min-width: 38rem;
      background-color: $color-white;
      border-radius: 4px;
      padding: 2px;
    }

    &--right {
      flex: 2;
      display: flex;
      flex-direction: column;
      gap: 1rem;
      padding: 2px;
      margin-right: 10px;

      > * {
        flex: 1;
        width: 100%;
        background-color: $color-white;
        border-radius: 4px;
      }
    }
  }

  &__bottom {
    margin-top: 2rem;
    margin-bottom: 2rem;
  }
}
</style>
